import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { apiClient } from '../services/apiClient';
import { createTvPlayback, TvPlaybackSnapshot } from '../utils/tvPlayback';
import { buildTvPlaylist } from '../utils/tvPlaylist';
import type { TvConfig } from '../utils/tvConfig';

// Decoding an offscreen Image warms the shared blob-URL decode cache, so the layer
// <img> paints without its own decode await (milestone ledger 2026-08-24).
const decodeImage = (url: string): Promise<void> => {
  const image = new Image();
  image.src = url;
  return image.decode?.() ?? Promise.resolve();
};

/**
 * Owns one TV playback engine for the component's lifetime and returns its live snapshot.
 * The slideshow runs only inside the non-interactive screensaver (any key dismisses it), so
 * it binds no keys and holds no wake lock: one would keep the TV out of standby past
 * sleep_timeout.
 */
export function useTvPlayback(config: TvConfig): TvPlaybackSnapshot {
  const playback = useMemo(() => createTvPlayback({
    // The device token is clamped to family server-side regardless; asking
    // explicitly keeps every client read mode-named.
    fetchManifest: (signal) => apiClient.getAllPhotos('family', signal),
    fetchMediaBlob: (photoId, signal) => apiClient.fetchMediaBlob(photoId, signal),
    decode: decodeImage,
    buildPlaylist: (manifest) => buildTvPlaylist(manifest, config.order),
    dwellMs: config.dwellMs,
    idleMs: config.idleMs,
  }), [config]);

  useEffect(() => {
    playback.start();
    return () => playback.dispose();
  }, [playback]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        playback.refreshManifest();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [playback]);

  return useSyncExternalStore(playback.subscribe, playback.getSnapshot);
}
